import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Divider,
  Chip,
} from '@mui/material';
import type { CostSummaryResponse } from '../../types/admin';

interface CostSummaryCardProps {
  data: CostSummaryResponse;
}

const formatCost = (cost: number | null): string => {
  if (cost === null || cost === undefined) return 'N/A';
  return `$${cost.toFixed(4)}`;
};

const formatTokens = (tokens: number): string => {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(1)}K`;
  return tokens.toString();
};

const CostSummaryCard: React.FC<CostSummaryCardProps> = ({ data }) => {
  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {/* Combined total */}
      <Grid size={{ xs: 12, md: 4 }}>
        <Card elevation={3}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Total Cost
            </Typography>
            <Typography variant="h4" color="primary" fontWeight="bold">
              {formatCost(data.combined.totalCost)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Chat costs */}
      <Grid size={{ xs: 12, md: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Chat Costs
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {formatCost(data.chat.totalCost)}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {data.chat.conversationCount} conversations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg: {formatCost(data.chat.avgCostPerConversation)} / conversation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatTokens(data.chat.totalTokens)} tokens
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Insurance costs */}
      <Grid size={{ xs: 12, md: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Insurance Costs
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {formatCost(data.insurance.totalCost)}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {data.insurance.reportCount} reports
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg: {formatCost(data.insurance.avgCostPerReport)} / report
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Extraction: {formatCost(data.insurance.extractionCost)} | Standardization: {formatCost(data.insurance.standardizationCost)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Cost by model */}
      {Object.keys(data.costByModel).length > 0 && (
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Cost by Model
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                {Object.entries(data.costByModel).map(([model, info]) => (
                  <Chip
                    key={model}
                    label={`${model}: ${formatCost(info.cost)} (${info.count} conversations, ${formatTokens(info.tokens)} tokens)`}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

export default CostSummaryCard;
